import { useEffect, useState, useRef, type FormEvent, type ChangeEvent } from "react";
import {
  ApiError,
  deleteMerchantLogo,
  getMerchantSettings,
  updateMerchantSettings,
  uploadMerchantLogo,
  type MerchantSettings,
} from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/DataTable";
import { ImageIcon, Trash2, Save, Upload, CheckCircle2 } from "lucide-react";

export function MerchantSettingsPage() {
  const [settings, setSettings] = useState<MerchantSettings | null>(null);
  const [phone, setPhone] = useState("");
  const [callbackUrl, setCallbackUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const s = await getMerchantSettings();
      setSettings(s);
      setPhone(s.business_phone ?? "");
      setCallbackUrl(s.callback_url ?? "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await updateMerchantSettings({ business_phone: phone || null, callback_url: callbackUrl || null });
      flash("Paramètres enregistrés");
      await load();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setError(null);
    try {
      await uploadMerchantLogo(f);
      flash("Logo uploadé");
      await load();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError("Erreur upload");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onRemoveLogo = async () => {
    if (!confirm("Supprimer le logo actuel ?")) return;
    await deleteMerchantLogo();
    flash("Logo supprimé");
    await load();
  };

  if (loading || !settings) return <div className="card card-body text-[#7e8299]">Chargement…</div>;

  return (
    <div>
      <PageHeader title="Paramètres" breadcrumbs={[{ label: "Mon espace" }, { label: "Paramètres" }]} />

      {success && (
        <div className="rounded-lg bg-success-50 text-success-700 px-4 py-3 mb-4 inline-flex items-center gap-2">
          <CheckCircle2 size={16} />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Logo */}
        <div className="card lg:col-span-1">
          <div className="card-header">
            <div className="card-title">Logo</div>
          </div>
          <div className="card-body">
            <div className="aspect-square rounded-xl bg-[#f5f8fa] border-2 border-dashed border-[#e4e6ef] flex items-center justify-center overflow-hidden mb-4">
              {settings.logo_data_url ? (
                <img src={settings.logo_data_url} alt={settings.business_name} className="max-h-full max-w-full object-contain p-4" />
              ) : (
                <ImageIcon size={48} className="text-[#cbd5e0]" />
              )}
            </div>
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onFile} className="hidden" />
            <div className="space-y-2">
              <button
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="btn btn-light-primary btn-sm w-full"
              >
                <Upload size={14} />
                {uploading ? "Upload…" : settings.logo_data_url ? "Remplacer" : "Téléverser"}
              </button>
              {settings.logo_data_url && (
                <button onClick={onRemoveLogo} className="btn btn-light btn-sm w-full text-danger-700">
                  <Trash2 size={14} />
                  Retirer le logo
                </button>
              )}
            </div>
            <div className="text-xs text-[#7e8299] mt-3 leading-relaxed">
              PNG, JPG, SVG ou WebP — 200 KB max. Affiché sur la page de paiement de vos clients.
            </div>
          </div>
        </div>

        {/* Business info */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <div className="card-title">Informations marchand</div>
          </div>
          <form onSubmit={onSave}>
            <div className="card-body space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Readonly label="Nom commercial" value={settings.business_name} />
                <Readonly label="Email business" value={settings.business_email} />
                <Readonly label="Pays" value={settings.country} />
                <Readonly
                  label="Mode"
                  value={<Badge color={settings.livemode ? "info" : "light"}>{settings.livemode ? "Livemode" : "Test"}</Badge>}
                />
              </div>

              <div className="border-t border-[#eff2f5] pt-5">
                <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-4">Modifiable</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Téléphone business</label>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} className="form-input" placeholder="+243..." maxLength={32} />
                  </div>
                  <div>
                    <label className="form-label">Callback URL (webhooks)</label>
                    <input
                      value={callbackUrl}
                      onChange={(e) => setCallbackUrl(e.target.value)}
                      className="form-input"
                      placeholder="https://api.votre-vrs.com/moko/callback"
                      maxLength={500}
                    />
                    <div className="form-hint">URL appelée par Moko pour notifier vos paiements.</div>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#eff2f5] pt-5">
                <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-3">Tarification (read-only)</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Readonly label="Commission mobile" value={`${(settings.commission_rate_mobile_bps / 100).toFixed(2)}%`} />
                  <Readonly label="Commission cards" value={`${(settings.commission_rate_cards_bps / 100).toFixed(2)}%`} />
                  <Readonly label="Délai refund" value={`${settings.refund_window_days} jours`} />
                </div>
                <div className="text-xs text-[#7e8299] mt-3">Contactez Moko pour modifier ces paramètres.</div>
              </div>

              {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
            </div>
            <div className="card-footer">
              <button type="submit" disabled={saving} className="btn btn-primary btn-sm ml-auto">
                <Save size={14} />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Readonly({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-1.5">{label}</div>
      <div className="text-sm text-[#181c32]">{value}</div>
    </div>
  );
}
